import React from 'react';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { TrendingUp, AlertCircle, Filter } from 'lucide-react';

const NotificationsPanel = ({ stats, lanes }) => {
  // Extract opportunities (high revenue impact from build_next)
  const opportunities = lanes?.build_next?.filter(d => d.revenue_impact >= 4).slice(0, 5) || [];
  
  // Extract risks (high churn risk from fix_now)
  const risks = lanes?.fix_now?.filter(d => d.churn_risk >= 4).slice(0, 5) || [];
  
  // Extract noise (from ignore lane)
  const noise = lanes?.ignore?.slice(0, 5) || [];

  return (
    <Card 
      className="h-[calc(100vh-100px)] flex flex-col"
      data-testid="notifications-panel"
    >
      <div className="p-4 border-b">
        <h2 className="text-sm font-semibold">Alerts</h2>
      </div>
      
      <Tabs defaultValue="opportunities" className="flex-1 flex flex-col">
        <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
          <TabsTrigger 
            value="opportunities" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
            data-testid="notifications-tab-opportunities"
          >
            <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
            Opportunities
            <Badge variant="secondary" className="ml-2 text-xs">
              {opportunities.length}
            </Badge>
          </TabsTrigger>
          
          <TabsTrigger 
            value="risks" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
            data-testid="notifications-tab-risks"
          >
            <AlertCircle className="h-3.5 w-3.5 mr-1.5" />
            Risks
            <Badge variant="secondary" className="ml-2 text-xs">
              {risks.length}
            </Badge>
          </TabsTrigger>
          
          <TabsTrigger 
            value="noise" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
            data-testid="notifications-tab-noise"
          >
            <Filter className="h-3.5 w-3.5 mr-1.5" />
            Noise
            <Badge variant="secondary" className="ml-2 text-xs">
              {noise.length}
            </Badge>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="opportunities" className="flex-1 m-0">
          <ScrollArea className="h-full">
            <div className="p-4 space-y-3">
              {opportunities.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p>No opportunities yet</p>
                </div>
              ) : (
                opportunities.map((decision, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="h-2 w-2 rounded-full bg-green-500 mt-1.5" />
                      <div className="flex-1">
                        <p className="text-xs font-semibold">{decision.summary}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {decision.why}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {decision.signal?.user_segment}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Revenue: {decision.revenue_impact}/5
                          </span>
                        </div>
                      </div>
                    </div>
                    {i < opportunities.length - 1 && <Separator />}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>
        
        <TabsContent value="risks" className="flex-1 m-0">
          <ScrollArea className="h-full">
            <div className="p-4 space-y-3">
              {risks.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p>No risks detected</p>
                </div>
              ) : (
                risks.map((decision, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="h-2 w-2 rounded-full bg-red-500 mt-1.5" />
                      <div className="flex-1">
                        <p className="text-xs font-semibold">{decision.summary}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {decision.why}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {decision.signal?.user_segment}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Churn: {decision.churn_risk}/5
                          </span>
                        </div>
                      </div>
                    </div>
                    {i < risks.length - 1 && <Separator />}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>
        
        <TabsContent value="noise" className="flex-1 m-0">
          <ScrollArea className="h-full">
            <div className="p-4 space-y-3">
              {noise.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  <Filter className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p>No noise filtered</p>
                </div>
              ) : (
                noise.map((decision, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="h-2 w-2 rounded-full bg-muted-foreground mt-1.5" />
                      <div className="flex-1">
                        <p className="text-xs font-semibold">{decision.summary}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {decision.why}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {decision.signal?.user_segment}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    {i < noise.length - 1 && <Separator />}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </Card>
  );
};

export default NotificationsPanel;
